import {utils, enumContainer} from '@wallaby-ui/core';
import {cloneDeep, debounce, forEach, isEmpty} from 'lodash';
import * as Plotly from 'plotly.js';
import {PlotHoverEvent, PlotMouseEvent, Layout, PlotData} from 'plotly.js';
import * as React from 'react';
import {defineMessages, injectIntl, WrappedComponentProps} from 'react-intl';
import createPlotlyComponent from 'react-plotly.js/factory';
import ResizeObserver from 'resize-observer-polyfill';
import {ChartReportConfig, ChartReportOptions} from 'src/js/model/reports_config';

import styles from './Chart.less';

const Plot = createPlotlyComponent(Plotly);

const I18N = defineMessages({
  EMPTY_DATA: {
    id: 'chart.emptyData',
    defaultMessage: 'No data to show',
    description: 'no data message',
  },
  DOWNLOAD_PLOT: {
    id: 'chart.downloadPlot',
    defaultMessage: 'Download plot',
    description: 'title text for download image button',
  },
});

const MARGIN_TOP_OFFSET = 40;

interface OwnProps {
  reportConfig: ChartReportConfig;
  reportData: Record<string, any>;
  enumMap?: enumContainer.EnumMap;
  onClick?: (onClickData: PlotMouseEvent) => void;
  onClickYTick?: (data: string) => void;
  onHover?: (event: PlotHoverEvent) => void;
  onUnhover?: (event: PlotMouseEvent) => void;
  downloadImageTitle?: string;
  additionalLayoutConfig?: Partial<Layout>;
  additionalDataConfig?: Partial<PlotData>[];
}

type Props = OwnProps & WrappedComponentProps;

class Chart extends React.PureComponent<Props> {
  static displayName = 'Chart';

  debouncedResize: () => void;

  cancelablePromises: utils.CancelablePromise<void>[];

  chartRef: React.RefObject<any>;

  chartWrapperRef: React.RefObject<HTMLDivElement>;

  ro: ResizeObserver | null;

  prevOnAfterPlot: React.MutableRefObject<((yTick: HTMLElement) => void) | null>;

  constructor(props) {
    super(props);

    this.debouncedResize = debounce(this.handleResize, 500).bind(this);
    this.handleDownload = this.handleDownload.bind(this);

    this.cancelablePromises = [];
    this.chartRef = React.createRef();
    this.chartWrapperRef = React.createRef();
    this.ro = null;
    this.prevOnAfterPlot = {current: null};
  }

  componentDidMount() {
    if (!isEmpty(this.chartWrapperRef.current)) {
      if (ResizeObserver) {
        this.ro = new ResizeObserver(this.debouncedResize);
        this.ro.observe(this.chartWrapperRef.current);
      }
    }
  }

  componentWillUnmount() {
    if (this.ro) {
      this.ro.disconnect();
    }

    forEach(this.cancelablePromises, (cancelablePromise) => {
      cancelablePromise.cancel();
    });
  }

  handleResize() {
    const cancelablePromise = utils.PromiseUtils.makeCancelable(Promise.resolve());
    this.cancelablePromises.push(cancelablePromise);
    cancelablePromise.promise
      .then(() => {
        if (this.chartRef.current && this.chartRef.current.el) {
          Plotly.Plots.resize(this.chartRef.current.el);
        }
      })
      .catch(utils.PromiseUtils.handleCanceledPromise);
  }

  handleDownload(gd) {
    // get duplicate of gd while we don't want to modify original element
    const newGd = gd.cloneNode(true);
    // assign values added by plotly as they are not cloned with cloneNode
    Object.assign(newGd, gd);

    const imageTitle = this.getImageTitle();
    if (imageTitle) {
      // add title and adjust layout to fit it
      const originalMarginTop = newGd.layout.margin?.t;
      let newGdMarginTop = originalMarginTop;
      let heightAdjustment = 0;
      // 12pt = 16px
      const fontSizeRatio = imageTitle.font.size / 12;
      const fontSizeInPixels = fontSizeRatio * 16;

      if (!originalMarginTop || originalMarginTop < fontSizeInPixels) {
        newGdMarginTop = fontSizeInPixels;
        heightAdjustment = fontSizeInPixels - (originalMarginTop ?? 0);
      }
      newGd.layout = {
        ...newGd.layout,
        title: imageTitle,
        ...(newGd.layout.height ? {height: newGd.layout.height + heightAdjustment + MARGIN_TOP_OFFSET} : {}),
        margin: {...newGd.layout.margin, t: newGdMarginTop + MARGIN_TOP_OFFSET},
      };
    }

    Plotly.downloadImage(newGd);
  }

  getImageTitle() {
    const {reportConfig, intl, downloadImageTitle} = this.props;
    const imageTitle = reportConfig?.getOptions()?.getDownloadedImageTitle();
    return imageTitle?.text || downloadImageTitle
      ? {
          text: downloadImageTitle ?? utils.TranslateUtils.translate(imageTitle.text, intl),
          font: {
            family: imageTitle?.family ?? 'Arial',
            size: imageTitle?.size ?? 24,
            color: imageTitle?.color ?? '#4f5e6e', // default value is var(--dlFont)
          },
        }
      : undefined;
  }

  render() {
    const {
      intl,
      reportConfig,
      reportData,
      enumMap,
      onClick,
      onClickYTick,
      onHover,
      onUnhover,
      additionalLayoutConfig = {},
      additionalDataConfig = {},
      downloadImageTitle: _,
      ...restProps
    } = this.props;

    let boundDataSection;

    // TODO: get from selector
    const data = reportData && reportData.data ? reportData.data : reportData;
    const dataSection = reportData && reportData.config ? reportData.config : reportConfig.getOptions().getData();

    const enhancedLayout = ChartReportOptions.mergePropsConfigWithLocalConfig(
      additionalLayoutConfig,
      reportConfig.getOptions().getLayout()
    );

    boundDataSection = ChartReportOptions.getBoundDataSection(data, reportConfig.getOptions().getDataBindings(), dataSection);

    boundDataSection = ChartReportOptions.translateLabels(
      boundDataSection,
      reportConfig.getOptions().getTranslationConfig(),
      enumMap,
      intl
    );

    boundDataSection = ChartReportOptions.mergePropsConfigWithLocalConfig(additionalDataConfig, boundDataSection);

    let layout = ChartReportOptions.localizeChartLayout(enhancedLayout, intl);
    layout = ChartReportOptions.trimAxisTickLabels(boundDataSection, layout);
    boundDataSection = ChartReportOptions.applyHoverTemplate(
      boundDataSection,
      layout,
      reportConfig.getOptions().getLayoutTextDimensionHoverLabel(),
      intl
    );

    if (!Array.isArray(boundDataSection)) {
      const tmp: any[] = [];
      for (const i in boundDataSection) {
        tmp.push(boundDataSection[i]);
      }
      boundDataSection = tmp;
    }

    const isNonEmpty = ChartReportOptions.isNonEmptyDataValues(boundDataSection);

    const options = reportConfig.getOptions();
    // eslint-disable-next-line no-nested-ternary
    const heightOfEmptyChart = layout.height
      ? layout.height
      : options.getStyle() && options.getStyle().height
      ? options.getStyle().height
      : 230;
    const optionsConfig = options.getConfig();
    const config = {
      ...optionsConfig,
      showTips: optionsConfig.showTips ?? false, // hide Plotly's "double-click to zoom out" notifier by default
      modeBarButtonsToRemove: [
        ...(optionsConfig.modeBarButtonsToRemove ?? []),
        'toImage', // remove original button for downloading image
        'lasso2d',
        'select2d',
        'autoScale2d',
      ],
      modeBarButtonsToAdd: [
        ...(optionsConfig.modeBarButtonsToAdd ?? []),
        {
          // add custom button for downloading image
          name: 'toImageCustom',
          title: utils.TranslateUtils.translate(I18N.DOWNLOAD_PLOT, intl),
          icon: Plotly.Icons.camera,
          click: this.handleDownload,
        },
      ],
    };

    const onAfterPlotCallback = (event: MouseEvent) => {
      const yTickData = (event.target as HTMLElement).getAttribute('data-unformatted') ?? '';
      if (onClickYTick) onClickYTick(yTickData);
    };

    console.log('Chart.boundDataSection', boundDataSection);
    console.log('Chart.layout', layout);
    console.log('Chart.config', config);
    return (
      <div {...restProps} className={styles.dlChart} ref={this.chartWrapperRef}>
        {reportConfig && reportConfig.getOptions() && dataSection && data?.length > 0 && isNonEmpty ? (
          <Plot
            ref={this.chartRef}
            // Plot component wrongly modifies its props, so we need to create duplicate here
            data={cloneDeep(boundDataSection)}
            // Plot component wrongly modifies its props, so we need to create duplicate here
            layout={cloneDeep(layout)}
            // Plot component wrongly modifies its props, so we need to create duplicate here
            config={cloneDeep(config)}
            style={options.getStyle()}
            className={options.getClass() + (onClickYTick ? ' hasOnClickYTick' : '')}
            useResizeHandler={options.getUseResizeHandler()}
            onClick={onClick}
            onHover={onHover}
            onUnhover={onUnhover}
            onAfterPlot={() => {
              const plotTag = this.chartRef?.current?.el;
              if (!plotTag) return;
              const yTickTags = [...plotTag.querySelectorAll('.yaxislayer-above text')];
              yTickTags.forEach((yTick) => {
                yTick.removeEventListener('click', this.prevOnAfterPlot);
                yTick.addEventListener('click', onAfterPlotCallback);
              });
              this.prevOnAfterPlot.current = onAfterPlotCallback;
            }}
          />
        ) : (
          <div className={styles.dlChartEmptyWrapper} style={{height: `${heightOfEmptyChart}px`}}>
            <div className={styles.dlChartEmptyData}>
              {options.getEmptyMessage()
                ? utils.TranslateUtils.translate({id: options.getEmptyMessage()}, intl)
                : utils.TranslateUtils.translate(I18N.EMPTY_DATA, intl)}
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default injectIntl(Chart);
